import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";

// Định nghĩa kiểu dữ liệu cho User và AuthContext
interface User {
  username: string;
  displayName: string;
  token: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (userInfo: User) => void;
  logout: () => void;
}

// Tạo AuthContext với giá trị mặc định
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook để sử dụng AuthContext dễ dàng hơn trong các component
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider để bao quanh ứng dụng và cung cấp thông tin đăng nhập
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  // Khi component khởi tạo, kiểm tra thông tin đăng nhập trong localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const displayName = localStorage.getItem('displayName') ?? "";
    if (token && username) {
      const decoded = jwtDecode(token || '');
      if (decoded.exp && decoded.exp > Date.now() / 1000) {
        setIsAuthenticated(true);
        setUser({ username, displayName, token });
      }
    }
  }, []);

  // Hàm login: Lưu token và username vào localStorage và cập nhật state
  const login = (userInfo: User) => {
    localStorage.setItem('token', userInfo.token);
    localStorage.setItem('username', userInfo.username);
    localStorage.setItem('displayName', userInfo.displayName);
    setIsAuthenticated(true);
    setUser(userInfo);
  };

  // Hàm logout: Xóa token và username trong localStorage và cập nhật state
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
